import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { Capacitor } from '@capacitor/core';
import { AIActive } from '../../../../../assets/svgs/AIActive';
import { Button } from '@/components/ui/button';
import { useAIDrawer } from '../../../../../hooks/useAIDrawer';
import { useSelector } from 'react-redux';
import { useLocation, useMatches } from 'react-router-dom';
import { resolveChatRouteId } from '../../../../../chatContext/useAppContextEnvelope';
import useWindowWidth from '../../../../../hooks/useWindowWidth';
import { useMatchMedia } from '../../../../../hooks/useMatchMedia';
import { OS_DESKTOP_MEDIA_QUERY } from '../../../../../os/osRouteMeta';
import { useHotkeys } from 'react-hotkeys-hook';
import type { ChatRouteId } from '../../../../../types/chatEnvelope';
import { selectActiveBlock } from '../../../../../slices/blocks/blocksSlice';
import type { RootState } from '../../../../../store/store';
import {
  useChatDocumentViewer,
  useHostedChatDocument,
} from '../../../../../hooks/useChatDocumentViewer';
import { ESCAPE_IGNORE_SELECTOR } from '../../../../../utils/escapeIgnoreSelector';

/**
 * Study surfaces keep the push drawer and its edge strip: they are read-and-ask
 * screens where the chat is part of the layout, and a floating window would
 * cover the very content the question is about (ORA-645).
 *
 * `self-assessment.block` is here for the reviewable attempt, which is not
 * suppressed: it is the NBME-style exam chrome, with its own dark theme and a
 * sticky footer a floating window would land on top of.
 */
const DRAWER_ROUTES: readonly ChatRouteId[] = [
  'study.qbank.block',
  'study.qbank.review',
  'study.flashcards',
  'study.video',
  'study.summary',
  'self-assessment.block',
  // The webinar's study phases are the only ones that reach here at all — the
  // host-led phases suppress the drawer outright — and they are study surfaces
  // with a presenter's screen beside them, so they keep the push drawer too.
  'webinar',
  // The course session docks the companion beside the step (Courses V1).
  'course.session',
];

/** The shell is desktop-web only; native and narrow viewports keep the sheet. */
const SHELL_MIN_WIDTH = 900;
// These implementations include the chat renderer, markdown/math support, and
// conversation state. They deliberately stay out of the protected shell until
// a student asks to open the companion.
const AIDrawer = lazy(() => import('..'));
const loadCompanionShell = () => import('../../CompanionShell');
const CompanionShell = lazy(loadCompanionShell);

const ShellLauncher = ({
  isHidden,
  onOpen,
}: {
  isHidden: boolean;
  onOpen: () => void;
}) => (
  <div className="companion-shell" data-chat-snapshot="exclude">
    <button
      type="button"
      className={`companion-shell_bubble ${isHidden ? 'is-hidden' : ''}`}
      onClick={onOpen}
      aria-label="Open AI Chat (c)"
      aria-hidden={isHidden}
      {...((isHidden ? { inert: '' } : {}) as Record<string, string>)}
    >
      <AIActive />
    </button>
  </div>
);

const AIDrawerLoadingShell = ({
  isShellLayout,
  onClose,
}: {
  isShellLayout: boolean;
  onClose: () => void;
}) =>
  isShellLayout ? (
    <div className="companion-shell" data-chat-snapshot="exclude">
      <div className="companion-shell_window" role="status" aria-live="polite">
        <div className="flex items-center justify-between gap-3 p-4">
          <span className="text-sm text-fg-secondary">Opening AI Chat…</span>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <div className="aid-drawer aid-drawer--has-context aid-drawer--open">
      <div className="aid-drawer-contents" role="status" aria-live="polite">
        <div className="flex items-center justify-between gap-3 p-4">
          <span className="text-sm text-fg-secondary">Opening AI Chat…</span>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );

const focusShellLauncher = (
  shouldFocusShellLauncherRef: MutableRefObject<boolean>
) => {
  if (!shouldFocusShellLauncherRef.current) return;

  const launcher = document.querySelector<HTMLButtonElement>(
    '.companion-shell_bubble:not(.is-hidden)'
  );

  if (!launcher) return;

  shouldFocusShellLauncherRef.current = false;
  launcher.focus({ preventScroll: true });
};

const ShellLauncherFocusRestorer = ({
  shouldFocusShellLauncherRef,
}: {
  shouldFocusShellLauncherRef: MutableRefObject<boolean>;
}) => {
  useEffect(() => {
    focusShellLauncher(shouldFocusShellLauncherRef);
  }, [shouldFocusShellLauncherRef]);

  return null;
};

/**
 * This component renders the AIDrawer UI and must be placed
 * inside the Router context (since the chat panel uses router hooks).
 *
 * The provider (AIDrawerProvider) manages state and can be
 * outside the router, but this renderer must be inside.
 */
const AIDrawerRenderer: React.FC = () => {
  const activeBlock = useSelector(selectActiveBlock);
  const { pathname } = useLocation();

  // The drawer stays hidden only while an actual self-assessment block is in
  // progress (the live timed attempt); once the attempt is complete
  // (reviewable) AI chat is allowed in review. Keyed off the block's own
  // selfAssessmentAttemptId rather than studyContext.returnTo so the
  // by-category/by-system review compositions (plain review blocks that still
  // return to the self-assessments page) get AI chat. Mirrors the gate in
  // ExamView.
  const isSelfAssessmentInProgress =
    Boolean(activeBlock?.selfAssessmentAttemptId) &&
    activeBlock?.selfAssessmentAccess?.isReviewable !== true;

  const matches = useMatches();
  const chatRoute = useMemo(() => resolveChatRouteId(matches), [matches]);
  const windowWidth = useWindowWidth();
  // The same query the OS shell mounts the desktop on, so the two can never
  // disagree. `window.innerWidth` is not that: on iPad Safari it follows the
  // visual viewport (pinch zoom, Stage Manager), and a desktop-width page
  // zoomed in reported sub-md — the shell kept the AI desktop up while the
  // companion un-suppressed its bubble over it.
  const isOsDesktopViewport = useMatchMedia(OS_DESKTOP_MEDIA_QUERY);
  // The companion's own document only: one on the AI desktop is the
  // desktop's, and never makes this the shell layout.
  const openDocument = useHostedChatDocument('companion');
  const { close: closeDocument } = useChatDocumentViewer();

  const isShellLayout =
    windowWidth > SHELL_MIN_WIDTH &&
    !Capacitor.isNativePlatform() &&
    (Boolean(openDocument) || !DRAWER_ROUTES.includes(chatRoute));

  const {
    setIsMounted,
    setLayout,
    setIsHotkeyEnabled,
    setIsSuppressed,
    isWebinarStudyPhase,
    isOpen,
    open,
    close,
    isAvailable,
    isPushModeAvailable,
    isHotkeyEnabled,
    shellMode,
    setShellMode,
    supportChannel,
    openHumanSupport,
    humanSupportError,
    isPageSuppressed,
    hasMobileNavRow,
    setHasMobileLauncher,
    intercomUnreadCount,
  } = useAIDrawer();

  const [hasOpened, setHasOpened] = useState(false);
  const shouldFocusShellLauncherRef = useRef(false);

  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  // The full-page chat route *is* the companion, so the drawer (and every
  // entry point that keys off `isAvailable`) stands down there. The games
  // section is public: a signed-in student gets the companion on it like any
  // other page (ORA-664), a guest playing Doctordle has no account to chat
  // from. The live webinar page joins the list (ORA-628) for its host-led
  // phases: a student following the presenter is not holding a side
  // conversation, and the drawer only crowded the screen. The page lifts that
  // for its study phases (QBank question, flashcards), where the drawer is as
  // useful as it is in ordinary study.
  // The AI desktop at / is the full chat page (legacy /chat redirects to it),
  // so the companion stands down there too — on desktop viewports only (the
  // shell's own `OS_DESKTOP_MEDIA_QUERY`): below the OS breakpoint there is no
  // desktop, the shell forwards / to the Dashboard, and the sheet must be able
  // to open on the way there (a study set's cold return lands on
  // `/?conversationId=…` and reopens its thread).
  // `isPageSuppressed` is a fullscreen game round asking for the screen.
  // Onboarding gates the app before any of it applies (ProtectedRoute): the
  // student has no exam, plan or data to ask about yet, so no companion.
  const isSuppressed =
    isPageSuppressed ||
    isSelfAssessmentInProgress ||
    pathname === '/onboarding' ||
    (pathname === '/' && isOsDesktopViewport) ||
    pathname === '/chat' ||
    (pathname.startsWith('/games') && !isAuthenticated) ||
    // A visitor reads the help docs on the marketing page, without a companion.
    (pathname.startsWith('/help') && !isAuthenticated) ||
    (pathname.startsWith('/study/webinar') && !isWebinarStudyPhase);

  // Nothing outside `ProtectedLayout` renders this component, so this is the one
  // place that can tell the provider the drawer exists on screen.
  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
      setIsSuppressed(false);
    };
  }, [setIsMounted, setIsSuppressed]);

  useEffect(() => {
    setIsSuppressed(isSuppressed);
  }, [isSuppressed, setIsSuppressed]);

  // Only the drawer reserves a gutter on the page, so the provider's body
  // classes follow the layout that is actually on screen.
  useEffect(() => {
    setLayout(!isSuppressed && isShellLayout ? 'shell' : 'drawer');
    return () => setLayout('drawer');
  }, [isShellLayout, isSuppressed, setLayout]);

  // Doctordle (and its archive) read bare letters off the window, so the `c`
  // hotkey stands down across the games section while the chat itself stays.
  useEffect(() => {
    setIsHotkeyEnabled(chatRoute !== 'games');
    return () => setIsHotkeyEnabled(true);
  }, [chatRoute, setIsHotkeyEnabled]);

  useEffect(() => {
    if (!isSelfAssessmentInProgress) return;
    close();
  }, [close, isSelfAssessmentInProgress]);

  // The drawer's own opener is the floating bubble, in place of the edge strip
  // the study drawer used to carry. Where the OS shell has a bottom nav row it
  // renders that bubble in the row instead, so this one stands down.
  const hasDrawerLauncher = !isSuppressed && !isShellLayout;

  const showDrawerLauncher =
    hasDrawerLauncher && !hasMobileNavRow && supportChannel !== 'human';

  useEffect(() => {
    setHasMobileLauncher(hasDrawerLauncher);
    return () => setHasMobileLauncher(false);
  }, [hasDrawerLauncher, setHasMobileLauncher]);

  // Calls to `open` also come from context consumers (chat pills, study-flow
  // returns, and app events), not only from this renderer's launcher. Record
  // every successful open so closing after the first load retains the same
  // conversation subtree on the next open.
  useEffect(() => {
    if (isOpen) setHasOpened(true);
  }, [isOpen]);

  // An open study drawer can hand its conversation to the shell's document
  // view at any moment (ORA-736). Fetch the shell's chunk ahead of that, so
  // the swap does not wait on the network. (Any hand-off is a transition that
  // keeps the drawer on screen until the chunk is in, so it never flashes a
  // placeholder; this only makes it quicker.)
  const canHandOffToShell =
    isOpen &&
    !isShellLayout &&
    DRAWER_ROUTES.includes(chatRoute) &&
    windowWidth > SHELL_MIN_WIDTH &&
    !Capacitor.isNativePlatform();

  useEffect(() => {
    if (canHandOffToShell) void loadCompanionShell();
  }, [canHandOffToShell]);

  const handleOpen = () => {
    setHasOpened(true);
    open();
  };

  const minimizeShell = useCallback(() => {
    const panel = document.querySelector('.companion-shell_window');

    shouldFocusShellLauncherRef.current = Boolean(
      panel && document.activeElement && panel.contains(document.activeElement)
    );

    setShellMode('bubble');
  }, [setShellMode]);

  // The shell implementation owns focus for its own buttons. The renderer
  // handles the global hotkeys before and during the lazy load, so it restores
  // focus itself for those paths.
  useEffect(() => {
    if (shellMode !== 'bubble' || !shouldFocusShellLauncherRef.current) return;

    focusShellLauncher(shouldFocusShellLauncherRef);
  }, [shellMode]);

  // The implementation bodies are lazy, so these global shortcuts must stay
  // here. They retain the drawer/shell behavior before and after the body has
  // loaded, including a close while the first chunk is still arriving.
  useHotkeys(
    'c',
    () => {
      if (isShellLayout) {
        setHasOpened(true);
        if (shellMode === 'bubble') {
          setShellMode('window');
        } else {
          minimizeShell();
        }
        return;
      }

      if (isOpen) {
        close();
      } else {
        handleOpen();
      }
    },
    { enabled: isAvailable && isHotkeyEnabled }
  );

  useHotkeys(
    'esc',
    () => {
      if (!isOpen) return;

      // While a document is open, Escape closes the document and restores the
      // mode the chat was in; it never shrinks the split into the window.
      if (openDocument) {
        if (document.activeElement?.closest('[data-chat-document-viewer]')) {
          closeDocument();
          return;
        }

        if (isShellLayout) {
          if (document.querySelector(ESCAPE_IGNORE_SELECTOR)) return;
          closeDocument();
          return;
        }
      }

      if (isShellLayout) {
        if (document.querySelector(ESCAPE_IGNORE_SELECTOR)) return;
        if (shellMode === 'expanded') {
          setShellMode('window');
        } else {
          minimizeShell();
        }
        return;
      }

      close();
    },
    {
      enableOnFormTags: ['input', 'textarea', 'select'],
      enabled: isOpen,
    }
  );

  if (isSuppressed) return null;

  const shouldMountImplementation = hasOpened || isOpen;

  if (isShellLayout) {
    if (!shouldMountImplementation)
      return (
        <ShellLauncher
          isHidden={supportChannel === 'human'}
          onOpen={handleOpen}
        />
      );

    return (
      <Suspense
        fallback={
          isOpen ? <AIDrawerLoadingShell isShellLayout onClose={close} /> : null
        }
      >
        <CompanionShell />
        <ShellLauncherFocusRestorer
          shouldFocusShellLauncherRef={shouldFocusShellLauncherRef}
        />
      </Suspense>
    );
  }

  if (!shouldMountImplementation) {
    return showDrawerLauncher ? (
      <ShellLauncher isHidden={false} onOpen={handleOpen} />
    ) : null;
  }

  return (
    <Suspense
      fallback={
        isOpen ? (
          <AIDrawerLoadingShell isShellLayout={false} onClose={close} />
        ) : null
      }
    >
      <AIDrawer
        isOpen={isOpen}
        onOpen={handleOpen}
        onClose={close}
        isAvailable={isAvailable}
        isPushModeAvailable={isPushModeAvailable}
        onOpenHumanSupport={openHumanSupport}
        humanSupportError={humanSupportError}
        showLauncher={showDrawerLauncher}
        launcherUnreadCount={intercomUnreadCount}
        // The course session docks the companion for the whole session: the
        // header keeps only the Minimize button (Courses V1).
        headerMode={chatRoute === 'course.session' ? 'minimal' : 'full'}
      />
    </Suspense>
  );
};

export default AIDrawerRenderer;
